import { ProjectStatusEnum } from 'api-hooks/projects/common';
import { NavigationRoutes } from 'common/constants';
import { ApprovalVerdictStatusEnum } from 'common/constants/enum';
import { usePurchaseTabStatus } from 'legacy-modules/order/component/purchase-list-tabs';
import { useRefundTabStatus } from 'legacy-modules/refund/components/refund-list-tabs';
import { useReleaseTabStatus } from 'legacy-modules/refund/components/release-tab-list';
import { useSaleTabStatus } from 'legacy-modules/sales/component/sale-list-tabs';
import { useSaleTabStatus as useDocumentSaleTabStatus } from 'legacy-modules/sales/travel-document/components/sale-list-tabs';
import { useTourTabStatus } from 'legacy-modules/tour/tour-list/components/tour-list-tab';
import { useTransactionTabStatus } from 'legacy-modules/transactions/component/transaction-list-tab';
import { useRouter } from 'next/router';
import qs from 'qs';
import React from 'react';

export default function useGetListParamsValue() {
  const { pathname, query } = useRouter();
  const {
    allStatus: tourAllStatus,
    activeStatus: tourActiveStatus,
    needApprovalStatus: tourNeedApprovalStatus,
    completeStatus: tourCompleteStatus,
  } = useTourTabStatus();

  const {
    processedStatus: saleProcessedStatus,
    needApprovalStatus: saleNeedApprovalStatus,
    rejectedApprovalStatus: saleRejectedApprovalStatus,
    voidStatus: saleVoidStatus,
    refundStatus: saleRefundStatus,
    paidStatus: salePaidStatus,
    allStatus: saleAllStatus,
  } = useSaleTabStatus();

  const {
    processStatus: documentSaleProcessStatus,
    submittedStatus: documentSaleSubmittedStatus,
    needApprovalStatus: documentSaleNeedApprovalStatus,
    rejectedStatus: documentSaleRejectedStatus,
    completeStatus: documentSaleCompleteStatus,
    paidStatus: documentSalePaidStatus,
    canceledStatus: documentSaleCanceledStatus,
    allStatus: documentSaleAllStatus,
  } = useDocumentSaleTabStatus();

  const {
    processedStatus: purchaseProcessedStatus,
    needApprovalStatus: purchaseNeedApprovalStatus,
    rejectedApprovalStatus: purchaseRejectedApprovalStatus,
    voidStatus: purchaseVoidStatus,
    refundStatus: purchaseRefundStatus,
    paidStatus: purchasePaidStatus,
    allStatus: purchaseAllStatus,
  } = usePurchaseTabStatus();

  const {
    processedStatus: refundProcessedStatus,
    needApprovalStatus: refundNeedApprovalStatus,
    rejectedApprovalStatus: refundRejectedApprovalStatus,
    completeStatus: refundCompleteStatus,
    allStatus: refundAllStatus,
  } = useRefundTabStatus();

  const {
    processedStatus: releaseProcessedStatus,
    needApprovalStatus: releaseNeedApprovalStatus,
    rejectedApprovalStatus: releaseRejectedApprovalStatus,
    completeStatus: releaseCompleteStatus,
    allStatus: releaseAllStatus,
  } = useReleaseTabStatus();

  const {
    activeStatus: transactionActiveStatus,
    needApprovalStatus: transactionNeedApprovalStatus,
    rejectedApprovalStatus: transactionRejectedApprovalStatus,
    voidStatus: transactionVoidStatus,
    allStatus: transactionAllStatus,
  } = useTransactionTabStatus();

  const defaultStatus = React.useMemo(() => {
    switch (pathname) {
      case NavigationRoutes.ApprovalItemsList:
        return `${ApprovalVerdictStatusEnum.Pending}`;
      case NavigationRoutes.TourList:
        return tourAllStatus;
      case NavigationRoutes.TourSalesList:
      case NavigationRoutes.TicketSalesList:
      case NavigationRoutes.ActivitySalesList:
      case NavigationRoutes.CruiseSalesList:
      case NavigationRoutes.HotelSalesList:
      case NavigationRoutes.ProductSalesList:
        return saleAllStatus;
      case NavigationRoutes.TravelDocumentSalesList:
        return documentSaleAllStatus;
      case NavigationRoutes.FlightPurchasesList:
      case NavigationRoutes.HotelPurchasesList:
      case NavigationRoutes.CruisePurchasesList:
      case NavigationRoutes.ActivityPurchasesList:
      case NavigationRoutes.DocumentPurchasesList:
      case NavigationRoutes.ProductPurchasesList:
        return purchaseAllStatus;
      case NavigationRoutes.ProjectList:
        return `${ProjectStatusEnum.active}`;
      case NavigationRoutes.RefundList:
        return refundAllStatus;
      case NavigationRoutes.RefundReleaseUserList:
      case NavigationRoutes.RefundReleaseAgentList:
        return releaseAllStatus;
      case NavigationRoutes.AccountPayablesList:
      case NavigationRoutes.AccountReceivablesList:
      case NavigationRoutes.BalanceAdjustmentsList:
      case NavigationRoutes.BankTransactionsList:
      case NavigationRoutes.CashTransactionsList:
      case NavigationRoutes.OrderAdjustmentList:
      case NavigationRoutes.ReceiptsList:
      case NavigationRoutes.TransfersList:
        return transactionAllStatus;

      default:
        return undefined;
    }
  }, [
    documentSaleAllStatus,
    pathname,
    purchaseAllStatus,
    refundAllStatus,
    releaseAllStatus,
    saleAllStatus,
    tourAllStatus,
    transactionAllStatus,
  ]);

  const parsedQuery: any = qs.parse(query as unknown as string);

  const queryPaymentStatus = parsedQuery.filter?.payment_status;

  const queryStatus =
    parsedQuery.filter?.status ||
    queryPaymentStatus ||
    parsedQuery.status ||
    defaultStatus;

  const statusToKey = React.useMemo(() => {
    switch (pathname) {
      case NavigationRoutes.ApprovalItemsList:
        if (queryStatus === `${ApprovalVerdictStatusEnum.Pending}`) return '0';
        if (
          queryStatus ===
          `${ApprovalVerdictStatusEnum.Approved}|${ApprovalVerdictStatusEnum.Rejected}`
        )
          return '1';
        return '0';

      case NavigationRoutes.TourList:
        if (queryStatus === tourAllStatus) return '0';
        if (queryStatus === tourActiveStatus) return '1';
        if (queryStatus === tourNeedApprovalStatus) return '2';
        if (queryStatus === tourCompleteStatus) return '3';
        return '0';

      case NavigationRoutes.TourSalesList:
      case NavigationRoutes.TicketSalesList:
      case NavigationRoutes.ActivitySalesList:
      case NavigationRoutes.CruiseSalesList:
      case NavigationRoutes.HotelSalesList:
      case NavigationRoutes.ProductSalesList:
        if (queryStatus === saleAllStatus) return '0';
        if (queryStatus === saleProcessedStatus) return '1';
        if (queryStatus === saleNeedApprovalStatus) return '2';
        if (queryStatus === saleRejectedApprovalStatus) return '3';
        if (queryStatus === saleVoidStatus) return '4';
        if (queryStatus === saleRefundStatus) return '5';
        if (queryPaymentStatus === salePaidStatus) return '6';
        return '0';

      case NavigationRoutes.TravelDocumentSalesList:
        if (queryStatus === documentSaleAllStatus) return '0';
        if (queryStatus === documentSaleProcessStatus) return '1';
        if (queryStatus === documentSaleSubmittedStatus) return '2';
        if (queryStatus === documentSaleNeedApprovalStatus) return '3';
        if (queryStatus === documentSaleRejectedStatus) return '4';
        if (queryStatus === documentSaleCompleteStatus) return '5';
        if (queryPaymentStatus === documentSalePaidStatus) return '6';
        if (queryStatus === documentSaleCanceledStatus) return '7';
        return '0';

      case NavigationRoutes.FlightPurchasesList:
      case NavigationRoutes.HotelPurchasesList:
      case NavigationRoutes.CruisePurchasesList:
      case NavigationRoutes.ActivityPurchasesList:
      case NavigationRoutes.DocumentPurchasesList:
      case NavigationRoutes.ProductPurchasesList:
        if (queryStatus === purchaseAllStatus) return '0';
        if (queryStatus === purchaseProcessedStatus) return '1';
        if (queryStatus === purchaseNeedApprovalStatus) return '2';
        if (queryStatus === purchaseRejectedApprovalStatus) return '3';
        if (queryStatus === purchaseVoidStatus) return '4';
        if (queryStatus === purchaseRefundStatus) return '5';
        if (queryStatus === purchasePaidStatus) return '6';
        return '0';

      case NavigationRoutes.ProjectList:
        if (queryStatus === `${ProjectStatusEnum.active}`) return '0';
        if (
          queryStatus ===
          `${ProjectStatusEnum.pending_cancel_approval}|${ProjectStatusEnum.cancel_draft}`
        )
          return '1';
        if (queryStatus === `${ProjectStatusEnum.canceled}`) return '2';
        if (queryStatus === `${ProjectStatusEnum.completed}`) return '3';
        return '0';

      case NavigationRoutes.RefundList:
        if (queryStatus === refundAllStatus) return '0';
        if (queryStatus === refundProcessedStatus) return '1';
        if (queryStatus === refundNeedApprovalStatus) return '2';
        if (queryStatus === refundRejectedApprovalStatus) return '3';
        if (queryStatus === refundCompleteStatus) return '4';
        return '0';

      case NavigationRoutes.RefundReleaseUserList:
      case NavigationRoutes.RefundReleaseAgentList:
        if (queryStatus === releaseAllStatus) return '0';
        if (queryStatus === releaseProcessedStatus) return '1';
        if (queryStatus === releaseNeedApprovalStatus) return '2';
        if (queryStatus === releaseRejectedApprovalStatus) return '3';
        if (queryStatus === releaseCompleteStatus) return '4';
        return '0';

      case NavigationRoutes.AccountPayablesList:
      case NavigationRoutes.AccountReceivablesList:
      case NavigationRoutes.BalanceAdjustmentsList:
      case NavigationRoutes.BankTransactionsList:
      case NavigationRoutes.CashTransactionsList:
      case NavigationRoutes.OrderAdjustmentList:
      case NavigationRoutes.ReceiptsList:
      case NavigationRoutes.TransfersList:
        if (queryStatus === transactionAllStatus) return '0';
        if (queryStatus === transactionActiveStatus) return '1';
        if (queryStatus === transactionNeedApprovalStatus) return '2';
        if (queryStatus === transactionRejectedApprovalStatus) return '3';
        if (queryStatus === transactionVoidStatus) return '4';
        return '0';

      default:
        return '0';
    }
  }, [
    documentSaleAllStatus,
    documentSaleCanceledStatus,
    documentSaleCompleteStatus,
    documentSaleNeedApprovalStatus,
    documentSalePaidStatus,
    documentSaleProcessStatus,
    documentSaleRejectedStatus,
    documentSaleSubmittedStatus,
    pathname,
    purchaseAllStatus,
    purchaseNeedApprovalStatus,
    purchasePaidStatus,
    purchaseProcessedStatus,
    purchaseRefundStatus,
    purchaseRejectedApprovalStatus,
    purchaseVoidStatus,
    queryPaymentStatus,
    queryStatus,
    refundAllStatus,
    refundCompleteStatus,
    refundNeedApprovalStatus,
    refundProcessedStatus,
    refundRejectedApprovalStatus,
    releaseAllStatus,
    releaseCompleteStatus,
    releaseNeedApprovalStatus,
    releaseProcessedStatus,
    releaseRejectedApprovalStatus,
    saleAllStatus,
    saleNeedApprovalStatus,
    salePaidStatus,
    saleProcessedStatus,
    saleRefundStatus,
    saleRejectedApprovalStatus,
    saleVoidStatus,
    tourActiveStatus,
    tourAllStatus,
    tourCompleteStatus,
    tourNeedApprovalStatus,
    transactionActiveStatus,
    transactionAllStatus,
    transactionNeedApprovalStatus,
    transactionRejectedApprovalStatus,
    transactionVoidStatus,
  ]);

  const queryTab = statusToKey || '0';
  const queryPage = Number(query.page) || 1;

  return { queryTab, queryStatus, queryPage, queryPaymentStatus };
}
